import { FlightOffer } from "../types/flight";
import { FlightSearchState } from "../components/philippineAirlines/types";

const STORAGE_KEYS = {
    SEARCH_PARAMS: "philippineAirlines.searchParams",
    SEARCH_RESULTS: "philippineAirlines.searchResults",
    SEARCH_DICTIONARIES: "philippineAirlines.searchDictionaries",
    LAST_SEARCH: "philippineAirlines.lastSearch",
    FORM_STATE: "philippineAirlines.formState",
};

export interface StoredSearchData {
    searchParams: {
        origin: string;
        destination: string;
        tripType: string;
        departureDate: string;
        returnDate?: string;
        adults: number;
        children?: number;
        infants?: number;
        travelClass?: string;
    };
    searchResults: FlightOffer[];
    dictionaries?: Record<string, any>;
    timestamp: number;
}

// Save search data to sessionStorage
export const saveSearchData = (
    params: StoredSearchData["searchParams"],
    results: FlightOffer[],
    dictionaries?: Record<string, any>
): void => {
    try {
        const data: StoredSearchData = {
            searchParams: params,
            searchResults: results,
            dictionaries,
            timestamp: Date.now(),
        };

        sessionStorage.setItem(
            STORAGE_KEYS.SEARCH_PARAMS,
            JSON.stringify(params)
        );
        sessionStorage.setItem(
            STORAGE_KEYS.SEARCH_RESULTS,
            JSON.stringify(results)
        );
        if (dictionaries) {
            sessionStorage.setItem(
                STORAGE_KEYS.SEARCH_DICTIONARIES,
                JSON.stringify(dictionaries)
            );
        }
        sessionStorage.setItem(
            STORAGE_KEYS.LAST_SEARCH,
            data.timestamp.toString()
        );
    } catch (error) {
        console.error("Error saving to sessionStorage:", error);
    }
};

// Get search data from sessionStorage
export const getSearchData = (): StoredSearchData | null => {
    try {
        const params = sessionStorage.getItem(STORAGE_KEYS.SEARCH_PARAMS);
        const results = sessionStorage.getItem(STORAGE_KEYS.SEARCH_RESULTS);
        const dictionaries = sessionStorage.getItem(
            STORAGE_KEYS.SEARCH_DICTIONARIES
        );
        const timestamp = sessionStorage.getItem(STORAGE_KEYS.LAST_SEARCH);

        if (!params || !results || !timestamp) {
            return null;
        }

        return {
            searchParams: JSON.parse(params),
            searchResults: JSON.parse(results),
            dictionaries: dictionaries ? JSON.parse(dictionaries) : undefined,
            timestamp: parseInt(timestamp, 10),
        };
    } catch (error) {
        console.error("Error reading from sessionStorage:", error);
        return null;
    }
};

// Clear search data from sessionStorage
export const clearSearchData = (): void => {
    try {
        sessionStorage.removeItem(STORAGE_KEYS.SEARCH_PARAMS);
        sessionStorage.removeItem(STORAGE_KEYS.SEARCH_RESULTS);
        sessionStorage.removeItem(STORAGE_KEYS.SEARCH_DICTIONARIES);
        sessionStorage.removeItem(STORAGE_KEYS.LAST_SEARCH);
    } catch (error) {
        console.error("Error clearing sessionStorage:", error);
    }
};

// Save form state to localStorage (persists across sessions)
export const saveFormState = (formState: FlightSearchState): void => {
    try {
        localStorage.setItem(
            STORAGE_KEYS.FORM_STATE,
            JSON.stringify(formState)
        );
    } catch (error) {
        console.error("Error saving form state:", error);
    }
};

// Get form state from localStorage
export const getFormState = (): FlightSearchState | null => {
    try {
        const formState = localStorage.getItem(STORAGE_KEYS.FORM_STATE);
        return formState ? JSON.parse(formState) : null;
    } catch (error) {
        console.error("Error reading form state:", error);
        return null;
    }
};

// Check if search data is still valid (optional expiration)
export const isSearchDataValid = (maxAgeMinutes = 30): boolean => {
    try {
        const timestamp = sessionStorage.getItem(STORAGE_KEYS.LAST_SEARCH);
        if (!timestamp) return false;

        const lastSearch = parseInt(timestamp, 10);
        const now = Date.now();
        const ageMinutes = (now - lastSearch) / (1000 * 60);

        return ageMinutes < maxAgeMinutes;
    } catch (error) {
        console.error("Error checking search data validity:", error);
        return false;
    }
};
